import { onBeforeUnmount, ref } from 'vue'
import { useAuthReady } from '~/composables/useAuthReady'
import { categoriesToClear } from '~/utils/coreSubscriptionCategories'
import { createMemberChangeTracker } from '~/utils/memberChangeTracker'

/**
 * Core subscription management for the application
 *
 * Provides centralized control of app-wide Firebase subscriptions
 * - Manages auth subscription
 * - Sets up core data subscriptions based on user state
 * - Handles role-based subscriptions
 * - Ensures proper cleanup
 */
export function useCoreSubscriptions() {
  const {
    registerSubscription,
    clearSubscriptionsByCategory,
    clearAllSubscriptions,
    hasSubscriptionWithKey,
    clearSubscriptionByKey,
  } = useSubscriptions()

  const user = useCurrentUser()
  const memberStore = useMembersStore()
  const subkrewesStore = useSubKrewesStore()
  const notificationsStore = useNotificationsStore()
  const tasksStore = useTasksStore()
  const { initializeNotifications } = useNotifications()
  // Use Nuxt's event bus instead of window events
  const eventBus = useEventBus<{ memberId: string }>('member-subkrewe-changed')

  const initialized = ref(false)
  const notificationsInitialized = ref(false)
  const setupInProgress = ref(false)

  /**
   * Setup the auth state subscription
   * @returns Cleanup function
   */
  const setupAuthSubscription = () => {
    if (import.meta.dev) {
      console.warn('[CoreSubscriptions] Setting up auth state subscription')
    }

    // Only set up the auth subscription once
    if (hasSubscriptionWithKey('auth-main')) {
      return
    }

    const authUnsub = useAuth().subscribeToAuthChanges((authUser) => {
      if (import.meta.dev) {
        console.warn('[CoreSubscriptions] Auth state changed:', authUser ? 'logged in' : 'logged out')
      }

      if (authUser) {
        // User signed in - set up core subscriptions
        setupCoreSubscriptions().catch((error) => {
          console.error('[CoreSubscriptions] Error setting up subscriptions:', error)
        })
      }
      else {
        // User signed out - clean up subscriptions but keep auth subscription
        clearCoreSubscriptions()
      }
    })

    // Register with a specific key to prevent duplicate auth subscriptions
    registerSubscription(authUnsub, 'auth', 'auth-main')

    return () => {
      clearSubscriptionsByCategory('auth')
    }
  }

  /**
   * Clean up all subscriptions except auth, member and the app-level
   * initialization. Signing in also keeps the ones pages own (see
   * utils/coreSubscriptionCategories.ts).
   */
  const clearCoreSubscriptions = async ({ keepPageOwned = false } = {}) => {
    if (import.meta.dev) {
      console.warn('[CoreSubscriptions] Cleaning up core subscriptions (preserving auth, member and app-level subscriptions)')
    }

    // Get all categories and clear everything except auth and member
    const { getAllSubscriptionCategories, getActiveSubscriptionCount } = useSubscriptions()
    const categories = categoriesToClear(getAllSubscriptionCategories(), { keepPageOwned })

    console.warn(`[CoreSubscriptions] About to clear ${categories.length} subscription categories with ${getActiveSubscriptionCount()} total subscriptions`)

    // Now clear each category
    categories.forEach((category) => {
      const count = getActiveSubscriptionCount(category)
      if (count > 0) {
        console.warn(`[CoreSubscriptions] Clearing category "${category}" with ${count} subscriptions`)
        clearSubscriptionsByCategory(category)
      }
    })

    // Reset state
    notificationsInitialized.value = false

    // DON'T reset initialized flag here as we're keeping member subscription
    // initialized.value = false

    // Reset the notifications store initialized flag
    if (notificationsStore.initialized) {
      notificationsStore.$patch({ initialized: false })
    }
  }

  /**
   * Set up core subscriptions for the current user
   */
  const setupCoreSubscriptions = async () => {
    if (!user.value?.uid) {
      console.warn('[CoreSubscriptions] No user logged in, cannot setup subscriptions')
      return
    }

    // Prevent concurrent setups which cause duplicate subscriptions
    if (setupInProgress.value) {
      console.warn('[CoreSubscriptions] Setup already in progress, skipping')
      return
    }

    setupInProgress.value = true

    try {
      // Wait for auth token to be fully ready before any Firestore operations
      await useAuthReady()

      // Clear any existing subscriptions first (but not auth, member, the
      // app-level ones, or what the open page subscribed to)
      clearCoreSubscriptions({ keepPageOwned: true })

      if (import.meta.dev) {
        console.warn('[CoreSubscriptions] Setting up core subscriptions for user', user.value.uid)
      }

      // 1. Member subscription - this should come first as other subscriptions depend on it
      const memberSubKey = `member-${user.value.uid}`

      // Only set up member subscription if it doesn't already exist
      if (!hasSubscriptionWithKey(memberSubKey)) {
        console.warn(`[CoreSubscriptions] Setting up member subscription for user ${user.value.uid}`)
        const memberUnsubscribe = memberStore.subscribeToCurrentMember()
        if (memberUnsubscribe) {
          registerSubscription(memberUnsubscribe, 'member', memberSubKey)
          console.warn(`[CoreSubscriptions] Successfully registered member subscription with key: ${memberSubKey}`)
        }
        else {
          console.error('[CoreSubscriptions] Failed to create member subscription')
        }
      }
      else {
        console.warn(`[CoreSubscriptions] Reusing existing member subscription for ${user.value.uid}`)
      }

      // 2. Initialize notifications if not already done
      if (!notificationsInitialized.value) {
        // The notification store now handles registration internally,
        // and returns a wrapped unsubscribe function
        const notificationUnsubscribe = await initializeNotifications()

        if (notificationUnsubscribe) {
          // No need to register this subscription - the store already did
          notificationsInitialized.value = true
        }
        else {
          console.warn('[CoreSubscriptions] Failed to initialize notifications subscription')
        }
      }

      // 3. Wait for member data to be loaded with a retry mechanism
      await waitForMemberData()
    }
    finally {
      setupInProgress.value = false
    }
  }

  /**
   * Wait for the member record (live, with its SubKrewe resolved) and then
   * set up the SubKrewe and role-based subscriptions. It used to poll the
   * store ten times, 200ms apart, and give up after two seconds.
   */
  const waitForMemberData = async () => {
    const uid = user.value?.uid
    if (!uid) return

    const currentMember = await memberStore.loadMember(uid)
    // Signed out or switched meanwhile
    if (user.value?.uid !== uid) return
    if (!currentMember) {
      throw new Error('Failed to load member data')
    }

    setupSubkreweSubscriptions(currentMember)
    setupRoleBasedSubscriptions(currentMember)
    initialized.value = true
  }

  /**
   * Set up SubKrewe subscriptions based on user's membership
   */
  const setupSubkreweSubscriptions = (currentMember: Member) => {
    if (!user.value?.uid) return

    const { registerSubscription, hasSubscriptionWithKey, clearSubscriptionByKey, clearSubscriptionsByCategory } = useSubscriptions()
    const subkrewesStore = useSubKrewesStore()

    // Add extensive logging to see what's happening
    console.warn('[CoreSubscriptions] Setting up subkrewe subscriptions for',
      currentMember.member_type,
      currentMember.name,
      'subkrewe:', currentMember.subkrewe
        ? (typeof currentMember.subkrewe === 'string' ? currentMember.subkrewe : (currentMember.subkrewe.id || 'invalid-ref'))
        : 'none',
    )

    // IMPORTANT: Clear ALL subscription categories that might overlap first
    console.warn('[CoreSubscriptions] Clearing all existing subkrewe-related subscriptions')
    clearSubscriptionsByCategory('subkrewe-user')
    clearSubscriptionsByCategory('subkrewe-captain')
    clearSubscriptionsByCategory('subkrewe')

    // COMPLETELY SEPARATE HANDLING FOR CAPTAINS VS EVERYONE ELSE

    // 1. SEPARATE HANDLING FOR USER SUBKREWE SUBSCRIPTION

    // For captains, use direct document access (more reliable)
    if (currentMember.member_type === 'captain' && currentMember.subkrewe) {
      // Extract subkrewe ID safely
      let subkreweId: string | null = null
      try {
        if (typeof currentMember.subkrewe === 'string') {
          subkreweId = currentMember.subkrewe
          console.warn('[CoreSubscriptions] Captain subkrewe ID (string):', subkreweId)
        }
        else if (typeof currentMember.subkrewe === 'object' && currentMember.subkrewe !== null) {
          subkreweId = currentMember.subkrewe.id || null
          console.warn('[CoreSubscriptions] Captain subkrewe ID (object):', subkreweId)
        }
      }
      catch (error) {
        console.error('[CoreSubscriptions] Error extracting subkrewe ID:', error)
      }

      // Early return if can't get ID
      if (!subkreweId) {
        console.error('[CoreSubscriptions] Failed to extract valid subkrewe ID for captain')
        return
      }

      // FOR CAPTAINS: Use direct document subscription with a unique key
      const captainSubkreweKey = `captain-subkrewe-${subkreweId}`

      if (hasSubscriptionWithKey(captainSubkreweKey)) {
        console.warn('[CoreSubscriptions] Clearing existing captain subkrewe subscription')
        clearSubscriptionByKey(captainSubkreweKey)
      }

      // Use direct document subscription (bypassing subscribeToUserSubKrewes entirely)
      console.warn(`[CoreSubscriptions] Setting up DIRECT captain subkrewe subscription for ID: ${subkreweId}`)

      const subkreweUnsubscribe = subkrewesStore.subscribeToSubKrewe(subkreweId)
      if (subkreweUnsubscribe) {
        registerSubscription(subkreweUnsubscribe, 'subkrewe-captain', captainSubkreweKey)
        console.warn(`[CoreSubscriptions] Successfully registered captain subkrewe subscription with key: ${captainSubkreweKey}`)
      }
      else {
        console.error('[CoreSubscriptions] Failed to create direct captain subkrewe subscription')
      }
    }
    // For admins and regular members, use the standard role-based approach
    else {
      // Standard user subscription with role-based logic
      const subkreweSubKey = `user-subkrewes-${user.value.uid}`

      if (hasSubscriptionWithKey(subkreweSubKey)) {
        console.warn('[CoreSubscriptions] Clearing existing standard subkrewe subscription')
        clearSubscriptionByKey(subkreweSubKey)
      }

      console.warn(`[CoreSubscriptions] Setting up standard user subkrewe subscription for ${currentMember.member_type}`)

      const subkreweUnsubscribe = subkrewesStore.subscribeToUserSubKrewes()
      if (subkreweUnsubscribe) {
        registerSubscription(subkreweUnsubscribe, 'subkrewe-user', subkreweSubKey)
        console.warn(`[CoreSubscriptions] Successfully registered standard subkrewe subscription with key: ${subkreweSubKey}`)
      }
      else {
        console.error('[CoreSubscriptions] Failed to create standard subkrewe subscription')
      }
    }

    // 2. HANDLE MEMBERS SUBSCRIPTION SEPARATELY

    if (currentMember.subkrewe?.id) {
      const subkreweMembersKey = `subkrewe-members-${currentMember.subkrewe.id}`

      // Clear existing subscription if it exists
      if (hasSubscriptionWithKey(subkreweMembersKey)) {
        console.warn(`[CoreSubscriptions] Clearing existing members subscription for subkrewe ${currentMember.subkrewe.id}`)
        clearSubscriptionByKey(subkreweMembersKey)
      }

      console.warn(`[CoreSubscriptions] Setting up members subscription for subkrewe ${currentMember.subkrewe.id}`)
      const membersUnsubscribe = subkrewesStore.subscribeToSubKreweMembers(currentMember.subkrewe.id)
      if (membersUnsubscribe) {
        registerSubscription(membersUnsubscribe, 'subkrewe-members', subkreweMembersKey)
        console.warn(`[CoreSubscriptions] Successfully registered members subscription with key: ${subkreweMembersKey}`)
      }
      else {
        console.error('[CoreSubscriptions] Failed to create members subscription')
      }
    }
    else {
      console.warn('[CoreSubscriptions] No subkrewe ID available for members subscription')
    }

    // NOTE: the available-subkrewes listener is no longer set up here for
    // every user — UI that browses the full list (e.g. BrowserView) calls
    // subkrewesStore.ensureAvailableSubKrewesSubscription() on mount instead.

    console.warn('[CoreSubscriptions] Finished setting up all subkrewe subscriptions')
  }

  /**
   * Set up role-based subscriptions based on the member's role
   */
  const setupRoleBasedSubscriptions = (currentMember: Member) => {
    if (!user.value?.uid) return

    if (!currentMember) {
      console.warn('[CoreSubscriptions] Cannot setup role-based subscriptions: Current member not found')
      return
    }

    if (import.meta.dev) {
      console.warn('[CoreSubscriptions] Setting up role-based subscriptions for', currentMember.member_type)
    }

    // Set up task subscriptions with unique key
    const taskSubKey = `tasks-${user.value.uid}`

    // Always clear existing task subscriptions to prevent duplicates
    clearSubscriptionsByCategory('tasks')

    const taskUnsubscribers = tasksStore.subscribeToUserRelevantTasks({
      includeCompleted: true, // Ensures we see status changes to completed tasks
    })

    if (taskUnsubscribers && taskUnsubscribers.length > 0) {
      // Register each task subscription individually with a unique key
      taskUnsubscribers.forEach((unsub, index) => {
        registerSubscription(unsub, 'tasks', `${taskSubKey}-${index}`)
      })
    }

    // If the member is a captain, subscribe to their SubKrewe's tasks
    if (currentMember.member_type === 'captain' && currentMember.subkrewe?.id) {
      const subkreweId = currentMember.subkrewe.id
      const subkreweTasksKey = `subkrewe-tasks-${subkreweId}`

      // Always clear existing subkrewe tasks subscription if it exists
      if (hasSubscriptionWithKey(subkreweTasksKey)) {
        clearSubscriptionByKey(subkreweTasksKey)
      }

      const subkreweTasksUnsubscribe = tasksStore.subscribeToEntityTasks(
        subkreweId,
        'subkrewe',
        {
          includeCompleted: false,
          notifyOnChanges: true,
          category: `subkrewe-tasks-${subkreweId}`,
        },
      )

      if (subkreweTasksUnsubscribe) {
        registerSubscription(subkreweTasksUnsubscribe, 'subkrewe-tasks', subkreweTasksKey)
      }
    }

    // If the member is an admin or band admin, subscribe to admin tasks
    if (currentMember.roles?.admin || currentMember.roles?.band_admin) {
      const adminTasksKey = 'admin-tasks'

      // Always clear existing admin tasks subscription if it exists
      if (hasSubscriptionWithKey(adminTasksKey)) {
        clearSubscriptionByKey(adminTasksKey)
      }

      const adminTasksUnsubscribe = tasksStore.subscribeToTaskType('admin', {
        status: 'pending',
        includeCompleted: false,
        notifyOnChanges: true,
        category: 'admin-dashboard',
      })

      if (adminTasksUnsubscribe) {
        registerSubscription(adminTasksUnsubscribe, 'admin-tasks', adminTasksKey)
      }
    }

    // Those who read every member record get every member and SubKrewe
    // live, so the admin pages open on data that's already there
    const roles = currentMember.roles
    if (roles?.admin || roles?.band_admin || roles?.redshirt_admin) {
      useAdminLiveStore().start()
    }
  }

  /**
   * Set up store subscription to monitor member changes
   * This detects when member type or subkrewe changes and refreshes subscriptions
   */
  const setupMemberStoreSubscription = () => {
    // Check if we already have this subscription
    if (hasSubscriptionWithKey('member-store-subscription')) {
      return
    }

    // The comparison itself lives in createMemberChangeTracker, where it is
    // directly testable — see the note there for what it used to get wrong.
    const tracker = createMemberChangeTracker()

    const memberStoreUnsubscribe = memberStore.$subscribe((_mutation, state) => {
      if (!user.value?.uid) return

      // Get current member data
      const currentMember = state.members[`members/${user.value.uid}`]
      if (!currentMember) return

      const change = tracker.observe(currentMember)
      if (!change) return

      if (import.meta.dev) {
        console.warn(
          change.typeChanged
            ? `[CoreSubscriptions] Member type changed from ${change.previousType} to ${currentMember.member_type}, refreshing subscriptions`
            : '[CoreSubscriptions] Current user SubKrewe changed, refreshing subscriptions',
        )
      }
      setupCoreSubscriptions()
    })

    registerSubscription(memberStoreUnsubscribe, 'store-subscription', 'member-store-subscription')

    return () => {
      clearSubscriptionByKey('member-store-subscription')
    }
  }

  /**
   * Define refreshOnSubkreweChange function before it's used
   */
  const refreshOnSubkreweChange = ({ memberId }: { memberId: string }) => {
    if (!user.value?.uid) return

    // Only handle if it's the current user's change
    if (memberId === user.value.uid) {
      if (import.meta.dev) {
        console.warn('[CoreSubscriptions] Detected current user SubKrewe change, refreshing subscriptions')
      }
      setupCoreSubscriptions()
    }
  }

  /**
   * Subscribe to a specific entity's data
   * This allows components to request specific entity subscriptions
   * without managing the subscriptions themselves
   */
  const subscribeToEntity = (entityType: 'subkrewe', entityId: string): (() => void) | null => {
    if (!entityId) return null

    const subscriptionKey = `${entityType}-specific-${entityId}`

    // Check if we already have this subscription
    if (hasSubscriptionWithKey(subscriptionKey)) {
      return null
    }

    let unsubscribe: (() => void) | null = null

    // Handle entity-type specific subscriptions
    if (entityType === 'subkrewe') {
      unsubscribe = subkrewesStore.subscribeToSubKrewe(entityId)
    }

    // Register the subscription if successful
    if (unsubscribe) {
      registerSubscription(unsubscribe, `${entityType}-specific`, subscriptionKey)
      return () => clearSubscriptionByKey(subscriptionKey)
    }

    return null
  }

  /**
   * Initialize all subscriptions
   */
  const initialize = () => {
    // Add an app-level initialization guard
    const appInitKey = 'app-init-core-subscriptions'
    if (hasSubscriptionWithKey(appInitKey)) {
      if (import.meta.dev) {
        console.warn('[CoreSubscriptions] Already initialized at app level, skipping duplicate initialization')
      }
      return
    }

    // Set up auth subscription first
    const cleanupAuth = setupAuthSubscription()

    // Set up member store subscription to detect changes
    const cleanupStoreSubscription = setupMemberStoreSubscription()

    // Subscribe to the event bus instead of window event
    eventBus.on(refreshOnSubkreweChange)

    // If the user is already logged in on initialization, set up core subscriptions
    if (user.value?.uid) {
      if (import.meta.dev) {
        console.warn('[CoreSubscriptions] User already logged in on initialization, setting up subscriptions')
      }
      setupCoreSubscriptions()
    }

    // Create a dummy unsubscribe function to mark initialization
    const appInit = () => {
      if (import.meta.dev) {
        console.warn('[CoreSubscriptions] App-level initialization marker cleared')
      }
    }
    registerSubscription(appInit, 'app-init', appInitKey)

    // Return cleanup function for manual cleanup or component unmount
    return () => {
      // Remove the event bus listener instead of window event listener
      eventBus.off(refreshOnSubkreweChange)
      cleanupStoreSubscription?.()
      cleanupAuth?.()

      // Ensure we clear all subscriptions when cleaning up
      clearAllSubscriptions()
    }
  }

  /**
   * Get diagnostics about current subscription state
   */
  const getSubscriptionDiagnostics = () => {
    const { getAllSubscriptionCategories, getActiveSubscriptionCount, getActiveSubscriptions } = useSubscriptions()

    return {
      totalSubscriptions: getActiveSubscriptionCount(),
      categoryCounts: getAllSubscriptionCategories().map(category => ({
        category,
        count: getActiveSubscriptionCount(category),
      })),
      initialized: initialized.value,
      notificationsInitialized: notificationsInitialized.value,
      setupInProgress: setupInProgress.value,
      activeSubscriptions: getActiveSubscriptions(),
    }
  }

  // Set up automatic cleanup on component unmount
  const cleanup: (() => void) | undefined = initialize()

  onBeforeUnmount(() => {
    if (cleanup) {
      cleanup()
    }
  })

  return {
    initialized,
    setupCoreSubscriptions,
    clearCoreSubscriptions,
    clearAllSubscriptions,
    subscribeToEntity,
    getSubscriptionDiagnostics,
    setupAuthSubscription, // Add the auth subscription setup function
  }
}
